import { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Star, Trash2 } from 'lucide-react';
import type { ItemImage } from '@siliconvault/shared';
import { useTranslation } from '../../i18n';
import styles from './ImageLightbox.module.css';

interface ImageLightboxProps {
  images: ItemImage[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  title: string;
  canEdit: boolean;
  onSetPrimary?: (imageId: string) => void;
  onDelete?: (imageId: string) => void;
}

export function ImageLightbox({
  images,
  currentIndex,
  onIndexChange,
  onClose,
  title,
  canEdit,
  onSetPrimary,
  onDelete,
}: ImageLightboxProps) {
  const { t } = useTranslation();
  const current = images[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) onIndexChange(currentIndex - 1);
  }, [hasPrev, currentIndex, onIndexChange]);

  const goNext = useCallback(() => {
    if (hasNext) onIndexChange(currentIndex + 1);
  }, [hasNext, currentIndex, onIndexChange]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext]);

  if (!current) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      {/* Top bar */}
      <div className={styles.topBar} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <span className={styles.title}>{title}</span>
          {images.length > 1 && (
            <span className={styles.counter}>
              {currentIndex + 1} / {images.length}
            </span>
          )}
        </div>
        <div className={styles.topActions}>
          {canEdit && onSetPrimary && (
            <button
              className={`${styles.iconBtn} ${current.isPrimary ? styles.iconBtnActive : ''}`}
              onClick={() => !current.isPrimary && onSetPrimary(current.id)}
              title={current.isPrimary ? t('imageGallery.primaryBadge') : t('imageGallery.setPrimary')}
              disabled={current.isPrimary}
            >
              <Star size={18} fill={current.isPrimary ? 'currentColor' : 'none'} />
            </button>
          )}
          {canEdit && onDelete && (
            <button
              className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
              onClick={() => {
                const deletingIndex = currentIndex;
                onDelete(current.id);
                // Adjust index if we're at the end
                if (deletingIndex >= images.length - 1 && deletingIndex > 0) {
                  onIndexChange(deletingIndex - 1);
                }
                // Close if last image
                if (images.length <= 1) {
                  onClose();
                }
              }}
              title={t('imageGallery.deleteImage')}
            >
              <Trash2 size={18} />
            </button>
          )}
          <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div className={styles.imageArea} onClick={(e) => e.stopPropagation()}>
        {images.length > 1 && hasPrev && (
          <button className={`${styles.navBtn} ${styles.navPrev}`} onClick={goPrev}>
            <ChevronLeft size={22} />
          </button>
        )}

        <img
          key={current.id}
          src={current.imageUrl}
          alt={title}
        />

        {images.length > 1 && hasNext && (
          <button className={`${styles.navBtn} ${styles.navNext}`} onClick={goNext}>
            <ChevronRight size={22} />
          </button>
        )}
      </div>

      {/* Thumbnail bar */}
      {images.length > 1 && (
        <div className={styles.thumbnailBar} onClick={(e) => e.stopPropagation()}>
          {images.map((img, i) => (
            <div
              key={img.id}
              className={`${styles.thumb} ${i === currentIndex ? styles.thumbActive : ''}`}
              onClick={() => onIndexChange(i)}
            >
              <img src={img.imageUrl} alt="" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
